import React, { useState, useEffect, useRef } from 'react'
import { apiService } from '../services/apiService'
import { api } from '../services/authService'
import VehicleForm from '../components/VehicleForm'
import { formatCurrency } from '../utils/formatters'

// API Base URL for image paths
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  PlayIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import VehicleDetailModal from '../components/VehicleDetailModal'
import VehicleEditForm from '../components/VehicleEditForm'


const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [vehicleTypes, setVehicleTypes] = useState([])
  const [vehicleTypesMap, setVehicleTypesMap] = useState(new Map())
  const [searchQuery, setSearchQuery] = useState('')
  const [plateFilter, setPlateFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [showVehicleForm, setShowVehicleForm] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [editingVehicle, setEditingVehicle] = useState(null)
  const [sortField, setSortField] = useState('model')
  const [sortDirection, setSortDirection] = useState('asc')
  const [showExportMenu, setShowExportMenu] = useState(false)
  const exportMenuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Carregar tipos de veículos da API
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const response = await api.get('/api/vehicle-types')
        if (response.data && response.data.data) {
          setVehicleTypes(response.data.data)
          // Criar Map para lookup rápido
          const typesMap = new Map()
          response.data.data.forEach(type => {
            typesMap.set(type.name, {
              label: type.display_name,
              icon: type.icon || '🚛'
            })
          })
          setVehicleTypesMap(typesMap)
        }
      } catch (error) {
        console.error('Erro ao carregar tipos de veículos:', error)
      }
    }

    fetchVehicleTypes()
    loadVehicles()
  }, [])

  const loadVehicles = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/vehicles')
      const result = response.data
      console.log('Veículos carregados da API:', result.data)
      setVehicles(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar veículos:', error)
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.')
      } else {
        toast.error('Erro ao carregar veículos')
      }
      setVehicles([])
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const filteredVehicles = vehicles
    .filter(vehicle => {
      // Filtro de busca por nome do modelo
      const matchesSearch = !searchQuery ||
        vehicle.model.toLowerCase().includes(searchQuery.toLowerCase())

      // Filtro por placa
      const matchesPlate = !plateFilter ||
        vehicle.license_plate.toLowerCase().includes(plateFilter.toLowerCase())

      // Filtro por empresa locatária
      const matchesCompany = !companyFilter ||
        (vehicle.rented_by && vehicle.rented_by.toLowerCase().includes(companyFilter.toLowerCase()))

      const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter
      const matchesType = typeFilter === 'all' || vehicle.type === typeFilter

      return matchesSearch && matchesPlate && matchesCompany && matchesStatus && matchesType
    })
    .sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]

      // Tratamento para valores nulos (colocar no final)
      if (aVal === null || aVal === undefined) return 1
      if (bVal === null || bVal === undefined) return -1

      // Tratamento especial para campos específicos
      if (sortField === 'monthly_rate' || sortField === 'maintenance_score') {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

  const getStatusBadge = (status) => {
    const badges = {
      available: 'badge-success',
      rented: 'badge-warning',
      maintenance: 'badge-error',
      inactive: 'badge bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
    
    const labels = {
      available: 'Disponível',
      rented: 'Em Uso',
      maintenance: 'Manutenção',
      inactive: 'Inativo'
    }
    
    return <span className={`badge ${badges[status]}`}>{labels[status]}</span>
  }

  const getTypeIcon = (type) => {
    const typeData = vehicleTypesMap.get(type)
    return typeData ? typeData.icon : '🚛'
  }

  const getTypeLabel = (type) => {
    const typeData = vehicleTypesMap.get(type)
    return typeData ? typeData.label : type
  }

  const SortableHeader = ({ field, label }) => (
    <th
      onClick={() => handleSort(field)}
      className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortField === field && (
          sortDirection === 'asc' ?
            <ChevronUpIcon className="w-4 h-4" /> :
            <ChevronDownIcon className="w-4 h-4" />
        )}
      </div>
    </th>
  )

  // Função para obter a primeira imagem do veículo
  const getVehicleImage = (vehicle) => {
    if (vehicle.images && vehicle.images.length > 0) {
      return `${API_BASE_URL}${vehicle.images[0].url}`
    }
    return null
  }

  const handleAddVehicle = () => {
    setShowVehicleForm(true)
  }

  const handleCloseVehicleForm = () => {
    setShowVehicleForm(false)
  }

  const handleVehicleSuccess = () => {
    loadVehicles()
    setShowVehicleForm(false)
    toast.success('Veículo adicionado com sucesso!')
  }

  const handleViewVehicle = (id) => {
    const vehicle = vehicles.find(v => v.id === id)
    setSelectedVehicle(vehicle)
  }

  const handleEditVehicle = (id) => {
    const vehicle = vehicles.find(v => v.id === id)
    setEditingVehicle(vehicle)
  }

  const handleRentVehicle = (id) => {
    toast(`Iniciando locação do veículo ID: ${id}`, {
      icon: 'ℹ️',
    })
  }

  const handleTrackVehicle = (id) => {
    toast(`Rastreando veículo ID: ${id} via GETRAK GPS`, {
      icon: 'ℹ️',
    })
  }

  const handleScheduleMaintenance = (id) => {
    toast(`Agendando manutenção para veículo ID: ${id}`, {
      icon: 'ℹ️',
    })
  }

  const handleDeleteVehicle = async (id) => {
  if (!confirm('Tem certeza que deseja deletar este veículo? Esta ação não pode ser desfeita.')) {
    return
  }

  try {
    await api.delete(`/api/vehicles/${id}`)
    toast.success('Veículo deletado com sucesso!')
    // Remover da lista local e recarregar
    loadVehicles()

    // Se o veículo deletado estava sendo visualizado, fechar modal
    if (selectedVehicle && selectedVehicle.id === id) {
      setSelectedVehicle(null)
    }
  } catch (error) {
    console.error('Erro ao deletar veículo:', error)
    const errorData = error.response?.data
    const message = errorData?.details || errorData?.error || 'Erro ao deletar veículo'
    toast.error(message)
  }
}

  const statusLabels = { available: 'Disponível', rented: 'Em Uso', maintenance: 'Manutenção', inactive: 'Inativo' }

  const handleExportExcel = async () => {
    setShowExportMenu(false)
    try {
      const ExcelJS = (await import('exceljs')).default
      const workbook = new ExcelJS.Workbook()
      workbook.creator = 'VIPLOC System'
      workbook.created = new Date()
      const sheet = workbook.addWorksheet('Veículos')

      sheet.columns = [
        { header: 'Tipo', key: 'type', width: 22 },
        { header: 'Marca', key: 'brand', width: 15 },
        { header: 'Modelo', key: 'model', width: 22 },
        { header: 'Placa', key: 'license_plate', width: 12 },
        { header: 'Ano', key: 'year', width: 8 },
        { header: 'Status', key: 'status', width: 14 },
        { header: 'Locado Para', key: 'rented_by', width: 28 },
        { header: 'Mensalidade (R$)', key: 'monthly_rate', width: 18 },
      ]

      sheet.getRow(1).eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E40AF' } }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        }
      })

      filteredVehicles.forEach((v, i) => {
        const row = sheet.addRow({
          type: getTypeLabel(v.type),
          brand: v.brand || '-',
          model: v.model || '-',
          license_plate: v.license_plate || '-',
          year: v.year || '-',
          status: statusLabels[v.status] || v.status,
          rented_by: v.rented_by || '-',
          monthly_rate: parseFloat(v.monthly_rate) || 0,
        })
        if (i % 2 === 0) {
          row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }
          })
        }
        row.getCell('monthly_rate').numFmt = '#,##0.00'
      })

      const totalRow = sheet.addRow({})
      totalRow.getCell('model').value = `Total: ${filteredVehicles.length} veículo(s)`
      totalRow.getCell('model').font = { bold: true, italic: true }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `veiculos_${new Date().toISOString().split('T')[0]}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Exportação Excel concluída!')
    } catch (err) {
      console.error('Erro ao exportar Excel:', err)
      toast.error('Erro ao exportar Excel')
    }
  }

  const handleExportPdf = async () => {
    setShowExportMenu(false)
    try {
      const { default: jsPDF } = await import('jspdf')
      const { default: autoTable } = await import('jspdf-autotable')

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

      doc.setFontSize(16)
      doc.setTextColor(30, 64, 175)
      doc.text('VIPLOC - Relatório de Veículos', 14, 15)
      doc.setFontSize(9)
      doc.setTextColor(100, 100, 100)
      doc.text(
        `Gerado em: ${new Date().toLocaleDateString('pt-BR', { dateStyle: 'long' })}  |  Total: ${filteredVehicles.length} veículo(s)`,
        14, 22
      )

      autoTable(doc, {
        startY: 28,
        head: [['Tipo', 'Marca', 'Modelo', 'Placa', 'Ano', 'Status', 'Locado Para', 'Mensalidade']],
        body: filteredVehicles.map(v => [
          getTypeLabel(v.type),
          v.brand || '-',
          v.model || '-',
          v.license_plate || '-',
          v.year || '-',
          statusLabels[v.status] || v.status,
          v.rented_by || '-',
          formatCurrency(v.monthly_rate),
        ]),
        headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        alternateRowStyles: { fillColor: [239, 246, 255] },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: { 7: { halign: 'right' } },
      })

      doc.save(`veiculos_${new Date().toISOString().split('T')[0]}.pdf`)
      toast.success('Exportação PDF concluída!')
    } catch (err) {
      console.error('Erro ao exportar PDF:', err)
      toast.error('Erro ao exportar PDF')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando veículos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            🚛 Gestão de Veículos
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerencie sua frota completa com controle total
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(v => !v)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              <span>Exportar</span>
              <ChevronDownIcon className="w-4 h-4" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                <button
                  onClick={handleExportExcel}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <span>📊</span>
                  <span>Excel (.xlsx)</span>
                </button>
                <div className="border-t border-gray-100 dark:border-gray-700" />
                <button
                  onClick={handleExportPdf}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <span>📄</span>
                  <span>PDF (.pdf)</span>
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleAddVehicle}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Novo Veículo</span>
          </button>
        </div>

      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
              <div className="text-2xl">✅</div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Disponíveis</p>
              <p className="text-2xl font-bold text-green-600">
                {vehicles.filter(v => v.status === 'available').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
              <div className="text-2xl">🚛</div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Em Uso</p>
              <p className="text-2xl font-bold text-yellow-600">
                {vehicles.filter(v => v.status === 'rented').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
              <div className="text-2xl">🔧</div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Manutenção</p>
              <p className="text-2xl font-bold text-red-600">
                {vehicles.filter(v => v.status === 'maintenance').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <div className="text-2xl">📊</div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Taxa de Utilização</p>
              <p className="text-2xl font-bold text-blue-600">
                {vehicles.length > 0 ? Math.round((vehicles.filter(v => v.status === 'rented').length / vehicles.length) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search by Model Name */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por modelo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input pl-10"
            />
          </div>

          {/* Search by Plate */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar por placa..."
              value={plateFilter}
              onChange={(e) => setPlateFilter(e.target.value)}
              className="form-input"
            />
          </div>

          {/* Search by Company */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar por empresa..."
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="form-input"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todos os Status</option>
            <option value="available">Disponível</option>
            <option value="rented">Em Uso</option>
            <option value="maintenance">Manutenção</option>
            <option value="inactive">Inativo</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todos os Tipos</option>
            {vehicleTypes.filter(type => type.active).map(type => (
              <option key={type.id} value={type.name}>
                {type.icon ? `${type.icon} ` : ''}{type.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                  Tipo
                </th>
                <SortableHeader field="brand" label="Marca" />
                <SortableHeader field="model" label="Modelo" />
                <SortableHeader field="description" label="Descrição" />
                <SortableHeader field="license_plate" label="Placa" />
                <SortableHeader field="year" label="Ano Modelo" />
                <SortableHeader field="status" label="Status" />
                <SortableHeader field="rented_by" label="Locado Para" />
                <SortableHeader field="monthly_rate" label="Mensalidade" />
                <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredVehicles.map((vehicle) => (
                <tr key={vehicle.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  {/* Tipo */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    <div className="flex flex-col items-center">
                      <span className="text-xl">{getTypeIcon(vehicle.type)}</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {getTypeLabel(vehicle.type)}
                      </span>
                    </div>
                  </td>

                  {/* Marca */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {vehicle.brand}
                    </div>
                  </td>

                  {/* Modelo */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {vehicle.model}
                    </div>
                  </td>

                  {/* Descrição */}
                  <td className="px-2 py-3">
                    <div className="text-sm text-gray-600 dark:text-gray-400 max-w-[250px] line-clamp-2" title={vehicle.description || ''}>
                      {vehicle.description || '-'}
                    </div>
                  </td>

                  {/* Placa */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900 dark:text-white">
                      {vehicle.license_plate}
                    </div>
                  </td>

                  {/* Ano Modelo */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {vehicle.year}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    {getStatusBadge(vehicle.status)}
                  </td>

                  {/* Locado Para */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    {vehicle.rented_by ? (
                      <span className="text-sm text-gray-900 dark:text-white font-medium truncate block max-w-[150px]">
                        {vehicle.rented_by}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 italic">-</span>
                    )}
                  </td>

                  {/* Mensalidade */}
                  <td className="px-2 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(vehicle.monthly_rate)}
                    </div>
                  </td>

                  {/* Ações */}
                  <td className="px-2 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <button
                        onClick={() => handleViewVehicle(vehicle.id)}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="Visualizar"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditVehicle(vehicle.id)}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded transition-colors"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteVehicle(vehicle.id)}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Deletar"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredVehicles.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Nenhum veículo encontrado
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Tente ajustar os filtros de busca ou adicione um novo veículo
          </p>
          <button onClick={handleAddVehicle} className="btn btn-primary">
            Adicionar Primeiro Veículo
          </button>
        </div>
      )}

  {/* Modal do formulário */}
      {showVehicleForm && (
        <VehicleForm
          onClose={handleCloseVehicleForm}
          onSuccess={handleVehicleSuccess}
        />
      )}

      {/* ADICIONE AQUI - Modal de detalhes do veículo */}
      {selectedVehicle && (
        <VehicleDetailModal
          vehicle={selectedVehicle}
          onClose={() => setSelectedVehicle(null)}
          onEdit={(vehicle) => {
            setSelectedVehicle(null)
            setEditingVehicle(vehicle)
          }}
          onCreateContract={(vehicle) => {
            // Aqui você pode navegar para a página de criar contrato
            // Por enquanto, vamos apenas exibir uma mensagem
            toast.success(`Criar contrato para ${vehicle.model}`)
            setSelectedVehicle(null)
            // TODO: Implementar navegação para ContractForm com vehicle pré-selecionado
          }}
        />
      )}
      
      {/* Modal de edição do veículo */}
      {editingVehicle && (
        <VehicleEditForm
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSuccess={() => {
            loadVehicles()
            setEditingVehicle(null)
          }}
        />
      )}
    </div>  // <- NÃO MEXA nesta div
  )
}

export default VehiclesPage